// === PERSONENAUTO — coverage model ===
// The Hoofddekking variants, the optional-coverage descriptors (the toggle list), and the
// WA / WA+ / Volledig comparison-matrix rows. Mirrors rechtsbijstand/coverages.ts (RBV_COVERAGES).
// Labels are i18n keys (labelKey) resolved in the screen, exactly like the rechtsbijstand list.

/** The three hoofddekking (main-cover) levels, cheapest first. */
export type AutoVariant = 'WA' | 'WA+' | 'WA+volledig';
export const AUTO_VARIANTS: AutoVariant[] = ['WA', 'WA+', 'WA+volledig'];

/**
 * Whether the customer selected a casco variant (WA+ beperkt / WA+volledig). Only then
 * may the document carry CA rows; on WA-only every outgoing document keeps CA empty.
 */
export const hasCascoSelected = (dekkingen: { Hoofddekking?: string } | undefined): boolean =>
  ((dekkingen?.Hoofddekking || 'WA') as AutoVariant) !== 'WA';

/** One optional (add-on) coverage toggle. */
export interface AutoCoverageDescriptor {
  key: string;
  infoKey: string;
  labelKey: string;
  section: string;
  premiumField: string;
  cgIndex: number | undefined;
  cgCodes?: string[];
  requiresCasco: boolean;
  extraField: string | undefined;
  disabled: boolean;
  requiresYoungVehicle?: boolean;
}

export const AUTO_OPTIONAL_COVERAGES: AutoCoverageDescriptor[] = [
  { key: 'Rechtsbijstand', infoKey: 'legalAid', labelKey: 'screens.personenauto.coverage.legalAid', section: 'RP', premiumField: 'RP_BTP', cgIndex: undefined, requiresCasco: false, extraField: undefined, disabled: false },
  { key: 'Schade_Inzittenden', infoKey: 'passengerDamage', labelKey: 'screens.personenauto.coverage.passengerDamage', section: 'SV', premiumField: 'SV_BTP', cgIndex: undefined, requiresCasco: false, extraField: 'SV_VERZSOM', disabled: false },
  // Standard four (Rechtsbijstand + Schade Inzittenden above, Pechhulp + Vrije keuze
  // here) come first — they're the everyday add-ons. VKH greys out (not hides) on
  // WA-only so the advisor can still see what's on offer.
  { key: 'Pechhulp', infoKey: 'roadsideAssistance', labelKey: 'screens.personenauto.coverage.roadsideAssistance', section: 'PH', premiumField: 'PH_BTP', cgIndex: undefined, requiresCasco: false, extraField: undefined, disabled: false, requiresYoungVehicle: true },
  { key: 'Vrije_keuze_herstelbedrijf', infoKey: 'freeChoiceRepairShop', labelKey: 'screens.personenauto.coverage.freeChoiceRepairShop', section: 'CG', premiumField: 'CG_BTP', cgIndex: 0, cgCodes: ['2051'], requiresCasco: true, extraField: undefined, disabled: false },
  // Value-guarantee pair sits at the bottom. Allianz only prices these on Volledig
  // Casco (Beperkt comes back with CG_BTP=0), so the row is HIDDEN unless the gate
  // is fully met:
  //   NW  → WA+volledig + OB_EERSTEI=J + age ≤ 36 months
  //   AKW → WA+volledig + OB_EERSTEI=N + age ≤ 72 months
  // OB_EERSTEI guarantees only one of the two ever shows.
  { key: 'Nieuwwaarderegeling', infoKey: 'newValueScheme', labelKey: 'screens.personenauto.coverage.newValueScheme', section: 'CG', premiumField: 'CG_BTP', cgIndex: undefined, cgCodes: ['2040'], requiresCasco: true, extraField: undefined, disabled: false },
  { key: 'Aanschafwaarderegeling', infoKey: 'purchaseValueScheme', labelKey: 'screens.personenauto.coverage.purchaseValueScheme', section: 'CG', premiumField: 'CG_BTP', cgIndex: undefined, cgCodes: ['2041'], requiresCasco: true, extraField: undefined, disabled: false },
];

/** WA / WA+ / Volledig comparison matrix — which base cover includes each row. */
export interface AutoCoverageMatrixRow {
  key: string;
  wa: boolean;
  waPlus: boolean;
  waFull: boolean;
}

export const AUTO_COVERAGE_MATRIX_ROWS: AutoCoverageMatrixRow[] = [
  { key: 'damageToOthers',  wa: true,  waPlus: true,  waFull: true  },
  { key: 'claimAssistance', wa: true,  waPlus: true,  waFull: true  },
  { key: 'fireExplosion',   wa: false, waPlus: true,  waFull: true  },
  { key: 'theftBurglary',   wa: false, waPlus: true,  waFull: true  },
  { key: 'stormHailFlood',  wa: false, waPlus: true,  waFull: true  },
  { key: 'animals',         wa: false, waPlus: true,  waFull: true  },
  { key: 'windowDamage',    wa: false, waPlus: true,  waFull: true  },
  { key: 'collision',       wa: false, waPlus: false, waFull: true  },
  { key: 'vandalism',       wa: false, waPlus: false, waFull: true  },
];

/** Matrix column per variant; doubles as the `screens.personenauto.coverage.*` label key. */
export const AUTO_VARIANT_COLUMN: Record<AutoVariant, 'wa' | 'waPlus' | 'waFull'> = {
  WA: 'wa', 'WA+': 'waPlus', 'WA+volledig': 'waFull',
};

/** Matrix row keys the given hoofddekking covers (drives the highlights under the cards). */
export const coveredMatrixKeys = (variant: AutoVariant): string[] =>
  AUTO_COVERAGE_MATRIX_ROWS.filter((row) => row[AUTO_VARIANT_COLUMN[variant]]).map((row) => row.key);
